import { Component, Input, OnInit, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, FormControl } from '@angular/forms';

@Component({
  selector: 'app-dynamic-form-fields',
  templateUrl: './dynamic-form-fields.component.html',
  styleUrls: ['./dynamic-form-fields.component.scss']
})
export class DynamicFormFieldsComponent implements OnInit, OnChanges {
  fieldsConfigData: any;
  @Input() set fieldsConfig(value: any) {
    this.fieldsConfigData = value;
    this.initForm();
  }

  @Input() admissionReqData: any;
  @Output() formValueChange = new EventEmitter<any>();
  dynamicForm: FormGroup;

  constructor(private fb: FormBuilder) { }

  ngOnInit(): void {
    this.initForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes.fieldsConfig && !changes.fieldsConfig.firstChange) ||
        (changes.admissionReqData && !changes.admissionReqData.firstChange)) {
      this.initForm();
    }
  }

  private getIncomingConfigurableFields(): any[] {
    // Support both shapes:
    // 1) admissionReqData = [{ fieldName, selectedValue, ... }]
    // 2) admissionReqData = { configurable_forms: [{ fieldName, selectedValue, ... }] }
    if (Array.isArray(this.admissionReqData)) {
      return this.admissionReqData;
    }
    if (this.admissionReqData && Array.isArray(this.admissionReqData?.configurable_forms)) {
      return this.admissionReqData.configurable_forms;
    }
    return [];
  }

  private coerceToOption(options: any[], selected: any): any {
    if (!options || options.length === 0) return selected;
    // Try exact match first
    let match = options.find(o => o === selected);
    if (match !== undefined) return match;
    // Try stringified case-insensitive match
    const sel = selected != null ? String(selected).toLowerCase() : '';
    match = options.find(o => String(o).toLowerCase() === sel);
    if (match !== undefined) return match;
    // Boolean conveniences: map true/false -> 'true'/'false' or 'Yes'/'No'
    if (selected === true) {
      match = options.find(o => ['true', 'yes', '1'].includes(String(o).toLowerCase()));
      if (match !== undefined) return match;
    }
    if (selected === false) {
      match = options.find(o => ['false', 'no', '0'].includes(String(o).toLowerCase()));
      if (match !== undefined) return match;
    }
    return selected;
  }

  private normalizeSelectedValue(field: any, baseSelected: any): any {
    if (field.fieldTypeId === 'CHECKBOX') {
      const values = Array.isArray(field.values) ? field.values : [];
      if (values.length > 1) {
        if (Array.isArray(baseSelected)) return baseSelected;
        if (baseSelected == null || baseSelected === '') return [];
        if (typeof baseSelected === 'string') {
          return baseSelected.split(',').map(s => s.trim()).filter(s => s);
        }
        return [];
      } else {
        return baseSelected === true || baseSelected === 'true';
      }
    }
    if (field.fieldTypeId === 'RADIO_BUTTON') {
      const values = Array.isArray(field.values) ? field.values : [];
      return this.coerceToOption(values, baseSelected);
    }
    return baseSelected ?? '';
  }

  initForm(): void {
    this.dynamicForm = this.fb.group({});

    const configurableForms = this.getIncomingConfigurableFields();
    if (!Array.isArray(this.fieldsConfigData)) return;

    this.fieldsConfigData.forEach(field => {
      const matched = configurableForms.find((f: any) => f.fieldName === field.fieldName);
      const baseSelected = matched ? matched.selectedValue : field.selectedValue;
      const initialValue = this.normalizeSelectedValue(field, baseSelected);
      this.dynamicForm.addControl(field.fieldName, new FormControl(initialValue));
    });

    // Emit initial payload right after form initialization
    this.emitCurrentValues();

    this.dynamicForm.valueChanges.subscribe(obj => {
      const result = this.fieldsConfigData.map(field => {
        const v = obj[field.fieldName];
        if (field.fieldTypeId === 'CHECKBOX') {
          if (Array.isArray(field.values) && field.values.length > 1) {
            field.selectedValue = Array.isArray(v) ? v : [];
          } else {
            field.selectedValue = !!v;
          }
        } else {
          field.selectedValue = v;
        }
        return field;
      });
      this.formValueChange.emit(result);
    });
  }

  private emitCurrentValues(): void {
    if (!this.dynamicForm || !this.fieldsConfigData) return;
    const obj = this.dynamicForm.getRawValue();
    const result = this.fieldsConfigData.map((field: any) => {
      const v = obj[field.fieldName];
      if (field.fieldTypeId === 'CHECKBOX') {
        if (Array.isArray(field.values) && field.values.length > 1) {
          field.selectedValue = Array.isArray(v) ? v : [];
        } else {
          field.selectedValue = !!v;
        }
      } else {
        field.selectedValue = v;
      }
      return field;
    });
    this.formValueChange.emit(result);
  }

  onCheckboxChange(event: Event, controlName: string, option: string) {
    const input = event.target as HTMLInputElement;
    const ctrl = this.dynamicForm.get(controlName);
    const current: string[] = (ctrl?.value || []).slice();
    if (input.checked) {
      if (!current.includes(option)) current.push(option);
    } else {
      const idx = current.indexOf(option);
      if (idx > -1) current.splice(idx, 1);
    }
    ctrl?.setValue(current);
    ctrl?.markAsDirty();
    ctrl?.markAsTouched();
  }
}
